import api from "src/shared/api";
import { EQueryTagTypes } from "src/shared/types/api";
import {
  ICreateTargetingSettingPayload,
  IUpdateTargetingSettingPayload,
  TCountriesDropdownResponse,
  TServicesDropdownResponse,
  TTargetingSettingResponse,
  TTargetingSettingsResponse,
} from "../types/types";

export const targetingSettingsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getTargetingCountriesDropdown: build.query<
      TCountriesDropdownResponse,
      void
    >({
      query: () => ({
        url: "/api/targeting-settings/dropdown/countries",
        method: "GET",
      }),
    }),

    getTargetingServicesDropdown: build.query<TServicesDropdownResponse, void>({
      query: () => ({
        url: "/api/targeting-settings/dropdown/services",
        method: "GET",
      }),
    }),

    getTargetingSettingsList: build.query<TTargetingSettingsResponse, void>({
      query: () => ({
        url: "/api/targeting-settings",
        method: "GET",
      }),
      providesTags: (response) => {
        if (!response?.data) {
          return [
            {
              type: EQueryTagTypes.TARGETING_SETTINGS,
              id: "LIST",
            },
          ];
        }

        return [
          {
            type: EQueryTagTypes.TARGETING_SETTINGS,
            id: "LIST",
          },
          ...response.data.map((item) => ({
            type: EQueryTagTypes.TARGETING_SETTINGS,
            id: item.id,
          })),
        ];
      },
    }),

    getTargetingSetting: build.query<TTargetingSettingResponse, number>({
      query: (id) => ({
        url: `/api/targeting-settings/${id}`,
        method: "GET",
      }),
      providesTags: (_response, _error, id) => [
        {
          type: EQueryTagTypes.TARGETING_SETTINGS,
          id,
        },
      ],
    }),

    createTargetingSetting: build.mutation<
      TTargetingSettingResponse,
      ICreateTargetingSettingPayload
    >({
      query: (body) => ({
        url: "/api/targeting-settings",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        {
          type: EQueryTagTypes.TARGETING_SETTINGS,
          id: "LIST",
        },
      ],
    }),

    updateTargetingSetting: build.mutation<
      TTargetingSettingResponse,
      IUpdateTargetingSettingPayload
    >({
      query: ({ id, ...body }) => ({
        url: `/api/targeting-settings/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_response, _error, { id }) => [
        {
          type: EQueryTagTypes.TARGETING_SETTINGS,
          id,
        },
        {
          type: EQueryTagTypes.TARGETING_SETTINGS,
          id: "LIST",
        },
      ],
    }),

    deleteTargetingSetting: build.mutation<void, number>({
      query: (id) => ({
        url: `/api/targeting-settings/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_response, _error, id) => [
        {
          type: EQueryTagTypes.TARGETING_SETTINGS,
          id,
        },
        {
          type: EQueryTagTypes.TARGETING_SETTINGS,
          id: "LIST",
        },
      ],
    }),
  }),
});

export const {
  useGetTargetingCountriesDropdownQuery,
  useGetTargetingServicesDropdownQuery,
  useGetTargetingSettingsListQuery,
  useGetTargetingSettingQuery,
  useLazyGetTargetingSettingQuery,
  useCreateTargetingSettingMutation,
  useUpdateTargetingSettingMutation,
  useDeleteTargetingSettingMutation,
} = targetingSettingsApi;
